import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import { signedOutJoinRoute } from '../utils/joinRoute'

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView
  },
  {
    path: '/about/dalston',
    redirect: '/dalston'
  },
  {
    path: '/dalston',
    name: 'dalston',
    component: () => import('../views/DalstonView.vue')
  },
  {
    path: '/about/putney',
    redirect: '/putney'
  },
  {
    path: '/putney',
    name: 'putney',
    component: () => import('../views/PutneyView.vue')
  },
  {
    path: '/reformer-pilates-putney',
    name: 'reformer-pilates-putney',
    component: () => import('../views/ReformerPilatesPutneyView.vue')
  },
  {
    path: '/pricing',
    name: 'pricing',
    component: () => import('../views/PricingView.vue')
  },
  {
    path: '/franchise',
    name: 'franchise',
    component: () => import('../views/FranchiseView.vue')
  },
  {
    path: '/faqs',
    name: 'faqs',
    component: () => import('../views/FAQsView.vue')
  },
  {
    path: '/personal-training',
    name: 'trainers',
    component: () => import('../views/TrainersView.vue')
  },
  {
    path: '/personal-training/putney',
    name: 'pt-putney',
    component: () => import('../views/PersonalTrainingPutneyView.vue')
  },
  {
    path: '/personal-training/dalston',
    name: 'pt-dalston',
    component: () => import('../views/PersonalTrainingDalstonView.vue')
  },
  {
    path: '/find-a-pro',
    name: 'find-a-pt',
    component: () => import('../views/FindTrainersView.vue')
  },
  {
    // Self-service listing update for Pros already on Find a Pro.
    // No-indexed, not in main nav — surfaced from links on the
    // /gym-space-to-rent and /find-a-pro pages, and from
    // outbound emails Pros receive. Prerendered with `robots:
    // noindex, follow` and excluded from sitemap.xml.
    path: '/find-a-pro/update-your-listing',
    name: 'update-your-listing',
    component: () => import('../views/UpdateListingView.vue')
  },
  {
    // Free PT session campaign (src/data/freePtCampaign.js). Linked from the
    // campaign email and, while a run is promoted, the homepage and My PODs.
    // Prerendered with `robots: noindex,follow` and excluded from sitemap.xml.
    path: '/free-pt-session',
    name: 'free-pt-session',
    component: () => import('../views/FreePtSessionView.vue')
  },
  {
    // B2B landing page for PTs, coaches, therapists and Pilates
    // instructors looking for gym space to rent. On-page brand
    // stays "For Pros"; URL is keyword-led (SEO — "gym space to rent
    // in London") and lives at root, not under /personal-training.
    path: '/gym-space-to-rent',
    name: 'gym-space-to-rent',
    component: () => import('../views/ForProsView.vue')
  },
  {
    path: '/join',
    name: 'join',
    component: () => import('../views/JoinView.vue')
  },
  {
    path: '/book',
    name: 'book',
    meta: {
      requiresAuth: true
    },
    component: () => import('../views/BookView.vue')
  },
  {
    path: '/book/:location/:type',
    name: 'booktype',
    meta: {
      requiresAuth: true
    },
    component: () => import('../views/BookingPageView.vue')
  },
  {
    path: '/mypods',
    name: 'mypods',
    meta: {
      requiresAuth: true
    },
    component: () => import('../views/MyPodsView.vue')
  },
  {
    // /verify-email#<code> (new links) or /verify-email/<code> (older ones); either way
    // utils/verifyLinkCode.js takes the code and leaves the address as /verify-email.
    path: '/verify-email/:token?',
    name: 'verifyemail',
    component: () => import('../views/VerifyEmail.vue')
  },
  {
    path: '/reset_user_password/:id',
    name: 'password',
    component: () => import('../views/PasswordView.vue')
  },
  {
    path: '/offers/:offer_id',
    name: 'offers',
    component: () => import('../views/OffersView.vue')
  },
  {
    path: '/offices',
    name: 'offices',
    component: () => import('../views/OfficesView.vue')
  },
  {
    path: '/checkout',
    name: 'checkout',
    component: () => import('../views/CheckoutView.vue')
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import('../views/ContactView.vue')
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('../views/TermsView.vue')
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('../views/PrivacyView.vue')
  },
  {
    path: '/cookies',
    name: 'cookies',
    component: () => import('../views/CookiePolicyView.vue')
  },
  {
    // Catch-all — must stay LAST. Any path that isn't a route above
    // renders NotFoundView (noindex, no canonical). Render serves these
    // paths the dist/404.html fallback shell; see RENDER.md. Matching is
    // case-insensitive (vue-router default), so /Putney still resolves
    // to /putney rather than landing here.
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../views/NotFoundView.vue')
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Restore native back/forward scroll position.
    if (savedPosition) return savedPosition
    // Hash navigation (e.g. /putney#pods) — scroll to the anchor with
    // an offset that clears the fixed nav (88px tall, 72px when scrolled).
    if (to.hash) {
      return {
        el: to.hash,
        top: 96,
        behavior: 'smooth',
      }
    }
    // Skip the scroll-to-top call on initial page load (no `from` route).
    // The browser already places the page at top on a fresh document, and
    // vue-router's internal scrollTo here was registering as a 708 ms
    // forced reflow on Lighthouse mobile (its scrollTo reads pageYOffset
    // / layout each call, and the homepage has a deep section graph
    // making the layout calc expensive). Returning `false` means
    // vue-router skips the scroll entirely.
    if (!from.name) return false
    // For SPA navigations between prerendered routes we still want
    // top-of-page behaviour. `behavior: 'instant'` avoids the
    // smooth-animation path that reads layout on every frame.
    return { top: 0, behavior: 'instant' }
  },
})

// Ad attribution for the /register body (read by _readAttribution() in
// app/routes.js). Last paid click wins: a landing that carries utm_* or a
// click ID overwrites the stored record; an organic first hit only fills an
// empty or expired slot. Captured pre-consent, same as the gclid write below.
const ATTRIBUTION_STORE = 'gp_attribution';
const ATTRIBUTION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const ATTRIBUTION_QUERY_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'ad_id', 'placement'];

function captureAttribution(to, from) {
  try {
    const firstHit = !from.name;
    const paid = {};
    ATTRIBUTION_QUERY_KEYS.forEach((key) => {
      const v = Array.isArray(to.query[key]) ? to.query[key][0] : to.query[key];
      if (typeof v === 'string' && v) paid[key] = v;
    });
    if (!Object.keys(paid).length) {
      if (!firstHit) return;
      const stored = JSON.parse(localStorage.getItem(ATTRIBUTION_STORE) || 'null');
      if (stored && Date.now() - Date.parse(stored.captured_at) < ATTRIBUTION_MAX_AGE_MS) return;
    }
    // On a later in-app navigation window.location still holds the previous
    // page during beforeEach, so fall back to the router's target path.
    localStorage.setItem(ATTRIBUTION_STORE, JSON.stringify({
      ...paid,
      landing_page: firstHit ? window.location.pathname + window.location.search : to.fullPath.split('#')[0],
      referrer: document.referrer || undefined,
      captured_at: new Date().toISOString(),
    }));
  } catch (e) { /* storage unavailable (Safari Private) or bad JSON; never block navigation */ }
}

router.beforeEach((to, from, next) => {
  // Storage can throw (blocked storage, some private modes). A throw here
  // would fail the navigation and leave an empty page, so each access is
  // guarded on its own and never blocks navigation.
  try {
    if (to.query.gclid) {
      localStorage.setItem('gclid', to.query.gclid);
    }
  } catch (e) { /* storage unavailable: carry on */ }
  try {
    const gclAwMatch = document.cookie.split('; ').find(row => row.startsWith('_gcl_aw='));
    if (gclAwMatch) {
      localStorage.setItem('gcl_aw', gclAwMatch.split('=').slice(1).join('='));
    }
  } catch (e) { /* storage unavailable: carry on */ }
  if (typeof window !== 'undefined' && !window.__PRERENDER__) {
    captureAttribution(to, from);
  }
  // During the Puppeteer prerender pass, sessionStorage is empty on every
  // route — without this bypass, /book (and any future requiresAuth route)
  // would redirect to /join and the saved dist/book/index.html would carry
  // /join's canonical and title. Flag is set in scripts/prerender.js:199
  // via page.evaluateOnNewDocument() before the first navigation runs.
  if (typeof window !== 'undefined' && window.__PRERENDER__) {
    return next();
  }
  if (to.matched.some(record => record.meta.requiresAuth)) {
    // Unreadable session storage counts as signed out (same redirect).
    let signedIn = false;
    try {
      signedIn = !!sessionStorage.userSession;
    } catch (e) { /* storage unavailable: treat as signed out */ }
    if (!signedIn) {
      // Signed-out visitor clicking a booking CTA has declared booking intent —
      // skip the Login|Sign-up gate; JoinView reads ?show= in mounted(). Someone
      // who has had an account on this browser gets the log-in form, everyone
      // else the sign-up form (signedOutJoinRoute, shared with CheckoutView).
      next(signedOutJoinRoute());
    } else {
      next();
    }
  } else {
    next();
  }
})

// In-app navigations: let the tap that started one paint first (a pressed
// link, a closing menu), then build the new page. Navigating to a page whose
// code is already loaded (Home, any page visited before) otherwise renders
// the whole new view, its scroll and its page-view tags inside the tap.
// The first navigation and the prerender pass are unchanged; so is a hidden
// tab, which paints no frames.
router.beforeResolve((to, from) => {
  if (!from.name || window.__PRERENDER__ || document.visibilityState === 'hidden') return;
  return new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
})

export default router
